import { useEffect, useState } from "react";
import { useDragDraft } from "./draft";

export interface NumberFieldProps {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
  /** Extra work on drag start — draft batching is already handled. */
  onDragStart?: () => void;
  onDragEnd?: () => void;
  /** Opt out of automatic viewer draft batching (default on). */
  draft?: boolean;
  /** Number-box formatting (default: adaptive precision by magnitude). */
  format?: (v: number) => string;
  /** Suffix shown after the number box, e.g. "mm". */
  unit?: string;
  disabled?: boolean;
  className?: string;
}

function adaptive(v: number): string {
  if (Math.abs(v) >= 100) return v.toFixed(1);
  if (Math.abs(v) >= 10) return v.toFixed(2);
  if (Math.abs(v) >= 1) return v.toFixed(3);
  return v.toFixed(5);
}

/**
 * Numeric box + slider. The slider's drag is wrapped in viewer draft mode
 * automatically (see `./draft`), which is what the hand-rolled `CaratField`
 * existed to do. Values from either control are clamped to [min, max].
 */
export function NumberField({
  label, min, max, step, value, onChange,
  onDragStart, onDragEnd, draft, format = adaptive, unit, disabled, className = "",
}: NumberFieldProps) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);

  const drag = useDragDraft({ draft, onDragStart, onDragEnd });

  const commit = (raw: number) => {
    const v = Math.max(min, Math.min(max, Number.isNaN(raw) ? value : raw));
    setLocal(v);
    onChange(v);
  };

  return (
    <div className={`flex flex-col gap-1 ${disabled ? "opacity-50" : ""} ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-body text-[11px] text-on-surface-variant">{label}</span>
        <span className="flex items-center gap-1">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={format(local)}
            disabled={disabled}
            onChange={(e) => commit(parseFloat(e.target.value))}
            className="w-16 rounded-md border border-outline-variant/60 bg-surface-container-lowest px-1 py-0.5 text-right font-body text-[10px] outline-none focus:border-secondary"
          />
          {unit ? <span className="font-body text-[10px] text-outline">{unit}</span> : null}
        </span>
      </div>
      <input
        type="range"
        className="lux"
        min={min}
        max={max}
        step={step}
        value={local}
        disabled={disabled}
        {...drag}
        onChange={(e) => commit(parseFloat(e.target.value))}
      />
    </div>
  );
}
